/**
 * Leaderboard & game-summary page (M9.2, FR-10.1–10.3).
 *
 * A read-only surface served entirely off the isolated read path — it never opens a
 * WebSocket or touches a live GameActor, so viewing it can't affect live-game
 * latency (FR-10.3). Shows the global top-N (FR-10.2); with `?gameId=<id>` it also
 * shows that game's post-game standings (FR-10.1), the link the game-over screen
 * points at.
 *
 * Access (#285, D-285.4/D-285.6): the global top-N sits behind {@link LeaderboardGate}
 * — a signed-out/guest visitor sees a sign-in prompt instead of the board (no longer a
 * public exposure). The per-game summary stays OUTSIDE the gate so a guest who just
 * played can still reach their own game's standings from the game-over link.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { GameSummary, LeaderboardEntry } from '@jeopardy/shared';
import { fetchGameSummary, fetchLeaderboard } from '../net/leaderboard-api';
import { AppHeader } from '../ui/AppHeader';
import { Leaderboard } from '../ui/Leaderboard';
import { LeaderboardGate } from '../ui/LeaderboardGate';
import { useDocumentHead } from '../seo/useDocumentHead';
import { headModelFor } from '../seo/route-seo';

/** Format a (possibly negative) dollar score as `$N` / `-$N`. */
function money(score: number): string {
  return score < 0 ? `-$${Math.abs(score)}` : `$${score}`;
}

export function LeaderboardPage(): React.JSX.Element {
  const [params] = useSearchParams();
  const gameId = params.get('gameId');

  // Per-route SEO (#285: now `noindex`, D-285.5): the login-gated leaderboard is no longer
  // an SEO landing, so its head model carries a `noindex` robots directive (and the route is
  // dropped from prerender + sitemap). Self-canonical still omits the `?gameId=` query so
  // per-game summary views don't fragment into duplicate URLs.
  useDocumentHead(headModelFor('/leaderboard'));

  const [entries, setEntries] = useState<LeaderboardEntry[] | null>(null);
  const [summary, setSummary] = useState<GameSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchLeaderboard()
      .then((res) => {
        if (!cancelled) setEntries(res.entries);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Could not load the leaderboard');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!gameId) {
      setSummary(null);
      return;
    }
    let cancelled = false;
    void fetchGameSummary(gameId)
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch(() => {
        // A summary fetch failure is non-fatal — the leaderboard still renders.
        if (!cancelled) setSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [gameId]);

  return (
    <div className="app-shell">
      <AppHeader />
      <h1 className="leaderboard-title">Leaderboard</h1>

      {/* Per-game summary stays OUTSIDE the gate (D-285.6): a guest who just finished a
          game can still see their own standings via the game-over link. */}
      {gameId && (
        <section className="stack" data-testid="game-summary">
          <h2>Game summary</h2>
          {summary ? (
            <ol className="game-over__standings">
              {summary.standings.map((s) => (
                <li
                  key={s.playerId}
                  className="game-over__row"
                  data-testid={`summary-standing-${s.playerId}`}
                >
                  <span className="game-over__rank">#{s.rank}</span>
                  <span className="game-over__name">{s.name}</span>
                  <span
                    className={
                      'game-over__score' +
                      (s.score < 0 ? ' game-over__score--negative' : '')
                    }
                  >
                    {money(s.score)}
                  </span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="muted" data-testid="summary-missing">
              No summary found for that game.
            </p>
          )}
        </section>
      )}

      {/* The global top-N requires a registered account (#285, D-285.4). The leaderboard
          load + its error live inside the gate so neither is shown to a gated guest. */}
      <LeaderboardGate>
        {error && <div className="error-text">{error}</div>}
        <section className="stack">
          <h2>Top players</h2>
          {entries === null && !error ? (
            <p className="muted">Loading…</p>
          ) : (
            <Leaderboard entries={entries ?? []} />
          )}
        </section>
      </LeaderboardGate>
    </div>
  );
}
